import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Plus, X, Linkedin, Mail } from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const HERO_IMAGE = "/assets/images/children.webp";

const boardMembers = [
  {
    id: 1,
    name: "Rt. Hon. Dr. Anietie Etuk",
    role: "Executive Chairman",
    image:
      "https://customer-assets.emergentagent.com/job_video-study-build/artifacts/fi0cpp72_499196020_2174831292960592_6331174541859266834_n.jpg",
    bio: "Leads the Board's overall policy direction and strategic implementation of the Universal Basic Education programme across Akwa Ibom State.",
  },

  {
    id: 2,
    name: "Mrs. Idongesit Akpan",
    role: "Permanent Member I — Schools Services",
    image: "/assets/images/image2.jpeg",
    bio: "Oversees Schools Services, ensuring quality teaching and learning across all public primary and junior secondary schools.",
  },

  {
    id: 3,
    name: "Mr. Otobong Udo",
    role: "Permanent Member II — Finance",
    image: "/assets/images/image1.jpeg",
    bio: "Drives prudent financial management, accountability, and timely execution of intervention budgets across all 31 LGAs.",
  },

  {
    id: 4,
    name: "Mrs. Ekaette Bassey",
    role: "Director, Quality Assurance",
    image: "/assets/images/image2.jpeg",
    bio: "Champions monitoring, evaluation and quality assurance frameworks that lift learning outcomes statewide.",
  },

  {
    id: 5,
    name: "Mr. Ime Etim",
    role: "Director, Planning Research & Statistics",
    image: "/assets/images/image1.jpeg",
    bio: "Coordinates data-driven planning, education research and statistical reporting that guide every Board decision.",
  },

  {
    id: 6,
    name: "Mrs. Nsima Williams",
    role: "Director, Social Mobilization & ICT",
    image: "/assets/images/image2.jpeg",
    bio: "Leads community engagement and digital transformation that bring more children into school and online learning.",
  },
];

const MemberCard = ({ m, onOpen }) => (
  <article className="member-card group relative w-full max-w-[320px] h-[390px] bg-white overflow-hidden rounded-[22px] shadow-[0_18px_45px_rgba(0,0,0,0.18)]">
    <img
      src={m.image}
      alt={m.name}
      className="absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 group-hover:grayscale"
    />

    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

    <div className="absolute left-5 right-5 bottom-[72px] text-white">
      <h3
        className="text-[18px] md:text-[20px] font-black leading-tight"
        style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
      >
        {m.name}
      </h3>

      <p className="text-[13px] text-white/85 mt-1">{m.role}</p>
    </div>

    <button
      onClick={() => onOpen(m)}
      aria-label={`More about ${m.name}`}
      className="absolute left-0 bottom-0 h-[50px] w-[105px] bg-[#ff6600] flex items-center justify-center text-white hover:bg-[#0b6b1d] transition-colors"
      style={{
        clipPath: "polygon(0 0, 100% 0, 88% 100%, 0 100%)",
      }}
    >
      <span className="w-8 h-8 rounded-full bg-white text-[#ff6600] flex items-center justify-center">
        <Plus size={18} strokeWidth={3} />
      </span>
    </button>
  </article>
);

const MemberModal = ({ member, onClose }) => {
  if (!member) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-white shadow-2xl grid md:grid-cols-2 gap-0 max-h-[90vh] overflow-y-auto rounded-[26px]"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-[#ff6600] hover:text-white text-[#0b6b1d] flex items-center justify-center transition-colors z-10"
        >
          <X size={18} />
        </button>

        <div className="group relative h-full min-h-[420px] bg-slate-200 overflow-hidden">
          <img
            src={member.image}
            alt={member.name}
            className="w-full h-full object-cover object-top transition-all duration-700 group-hover:grayscale"
          />
        </div>

        <div className="p-7">
          <p className="text-[#ff6600] font-bold uppercase tracking-widest text-xs mb-2">
            {member.role}
          </p>

          <h3
            className="text-2xl md:text-3xl font-black text-[#0b6b1d] mb-4"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            {member.name}
          </h3>

          <p className="text-slate-600 leading-relaxed text-[15px]">
            {member.bio}
          </p>

          <div className="mt-6 flex gap-2">
            <a
              href="#"
              aria-label="LinkedIn"
              className="w-10 h-10 rounded-full bg-slate-100 hover:bg-[#ff6600] hover:text-white text-[#0b6b1d] flex items-center justify-center transition-colors"
            >
              <Linkedin size={16} />
            </a>

            <a
              href="#"
              aria-label="Email"
              className="w-10 h-10 rounded-full bg-slate-100 hover:bg-[#ff6600] hover:text-white text-[#0b6b1d] flex items-center justify-center transition-colors"
            >
              <Mail size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const BoardMembersPage = () => {
  const [selected, setSelected] = useState(null);

  const pageRef = useRef(null);

  const visibleMembers = boardMembers.slice(0, 6);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
        ease: "power3.out",
      });

      gsap.from(".member-card", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".members-grid",
          start: "top 85%",
        },
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef}>
      <section className="relative bg-white pb-24 overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-52 md:pb-60 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="AKSUBEB Board Members"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />

          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#0b6b1d]/75 to-[#ff6600]/35" />

          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#0b6b1d]/35 blur-3xl" />

          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />

          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />

              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Team Members
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Board Members
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Meet the team driving AKSUBEB&apos;s mandate — dedicated leaders
              shaping the future of basic education in Akwa Ibom State.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link
                to="/"
                className="hover:text-[#ff6600] transition-colors"
              >
                Home
              </Link>

              <ChevronRight size={14} />

              <Link
                to="/about"
                className="hover:text-[#ff6600] transition-colors"
              >
                About
              </Link>

              <ChevronRight size={14} />

              <span className="text-[#ff6600]">Board Members</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 -mt-36 md:-mt-44 z-10">
          <div className="members-grid flex flex-wrap justify-center gap-6">
            {visibleMembers.map((m) => (
              <MemberCard key={m.id} m={m} onOpen={setSelected} />
            ))}
          </div>
        </div>
      </section>

      <MemberModal member={selected} onClose={() => setSelected(null)} />
    </main>
  );
};

export default BoardMembersPage;